import React, { useState, useEffect, useMemo } from 'react';
import { InlineRating } from './InlineRating';
import { ReportAction } from './ReportAction';
import Disclaimer from '../../SubjectPage/table/Disclaimer';
import Toast from '../../../utils/Toast';
import logger from '../../../utils/logger';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

export function CreateTable({ resources }) {
  const [titleSearch, setTitleSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'modifiedAt', direction: 'descending' });
  const [reportSuccess, setReportSuccess] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingActionResource, setPendingActionResource] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [titleSearch, tagSearch, ratingFilter, subjectFilter, typeFilter, sortConfig]);

  useEffect(() => {
    if (reportSuccess) {
      const timer = setTimeout(() => setReportSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [reportSuccess]);

  const handleReportSuccess = () => {
    if (!reportSuccess) setReportSuccess(true);
  };

  // Derive unique subject list from results
  const uniqueSubjects = useMemo(() => {
    const subjects = new Set();
    resources.forEach(r => { if (r.subjectTitle) subjects.add(r.subjectTitle); });
    return Array.from(subjects).sort();
  }, [resources]);

  const filterResources = (resource) => {
    const titleMatch = !titleSearch || (resource.title && resource.title.toLowerCase().includes(titleSearch.toLowerCase()));
    const tagMatch = !tagSearch || (resource.tags && resource.tags.some(tag => tag.toLowerCase().includes(tagSearch.toLowerCase())));
    const ratingMatch =
      !ratingFilter ||
      (resource.averageRating !== undefined &&
        Math.floor(resource.averageRating) === parseInt(ratingFilter));
    const subjectMatch = !subjectFilter || resource.subjectTitle === subjectFilter;

    const url = resource.file_url || '';
    const isDoc = typeof url === 'string' && (
      url.includes('firebasestorage.googleapis.com') ||
      url.includes('appspot.com')
    );
    const typeMatch =
      typeFilter === 'all' ? true : typeFilter === 'documents' ? isDoc : (!isDoc && !!url);

    return titleMatch && tagMatch && ratingMatch && subjectMatch && typeMatch;
  };

  const sortResources = (items) => {
    if (!sortConfig.key) return items;
    const sorted = [...items].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'modifiedAt') {
        aValue = aValue ? (typeof aValue === 'string' ? new Date(aValue) : aValue.toDate()) : new Date(0);
        bValue = bValue ? (typeof bValue === 'string' ? new Date(bValue) : bValue.toDate()) : new Date(0);
      }

      if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getPublicUrl = (url) => {
    try {
      if (url.includes("/v0/b/")) return url;
      const filePath = url.split("share2teach-be.appspot.com/")[1].split("?")[0];
      const encodedPath = filePath.replace(/\//g, '%2F');
      return `https://firebasestorage.googleapis.com/v0/b/share2teach-be.appspot.com/o/${encodedPath}?alt=media`;
    } catch (error) {
      logger.error("Error converting URL:", error);
      return url;
    }
  };

  const handleResourceClick = (resource) => {
    setPendingActionResource(resource);
    setShowDisclaimer(true);
  };

  const acknowledgeDisclaimer = () => {
    if (pendingActionResource) {
      window.open(getPublicUrl(pendingActionResource.file_url), '_blank', 'noopener noreferrer');
      setPendingActionResource(null);
    }
    setShowDisclaimer(false);
  };

  const filteredResources = resources.filter(filterResources);
  const sortedResources = sortResources(filteredResources);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedResources.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * itemsPerPage;
  const paginatedResources = sortedResources.slice(startIdx, startIdx + itemsPerPage);

  const clearFilters = () => {
    setTitleSearch('');
    setTagSearch('');
    setRatingFilter('');
    setSubjectFilter('');
    setTypeFilter('all');
  };

  const hasActiveFilters = titleSearch || tagSearch || ratingFilter || subjectFilter || typeFilter !== 'all';

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1 inline opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>;
    return sortConfig.direction === 'ascending'
      ? <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1 inline text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
      : <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 ml-1 inline text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>;
  };

  return (
    <div className="w-full relative">
      <Toast
        message="Report submitted successfully"
        variant="success"
        visible={reportSuccess}
        onDismiss={() => setReportSuccess(false)}
      />

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white text-gray-900 p-6 md:p-8 rounded-2xl max-w-lg w-full shadow-2xl text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-600 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h2 className="text-lg font-bold mb-3">Important Disclaimer</h2>
            <div className="mb-6 text-sm text-gray-600">
              <Disclaimer />
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setShowDisclaimer(false); setPendingActionResource(null); }}
                className="px-5 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={acknowledgeDisclaimer}
                className="px-5 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
              >
                I Understand — Open Resource
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-100">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[160px]">
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input
              type="text"
              placeholder="Search titles…"
              value={titleSearch}
              onChange={(e) => setTitleSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
          </div>
          <div className="relative flex-1 min-w-[160px]">
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" /></svg>
            <input
              type="text"
              placeholder="Search tags…"
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
          </div>
          <select
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
            className="min-w-[130px] py-1.5 px-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
          >
            <option value="">All subjects</option>
            {uniqueSubjects.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="min-w-[130px] py-1.5 px-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
          >
            <option value="">All ratings</option>
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}+ ★</option>)}
          </select>
        </div>
      </div>

      {/* Type toggle + Clear + Count */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg p-0.5">
          {[
            { key: 'all', label: 'All' },
            { key: 'documents', label: 'Docs' },
            { key: 'links', label: 'Links' },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setTypeFilter(opt.key)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                typeFilter === opt.key
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            Clear filters
          </button>
        )}

        <p className="ml-auto text-xs text-gray-400">
          {sortedResources.length} result{sortedResources.length !== 1 ? 's' : ''}
          {hasActiveFilters ? ' (filtered)' : ''}
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block rounded-xl border border-gray-100 overflow-hidden shadow-card">
        <table className="w-full table-fixed text-sm text-left">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th onClick={() => handleSort('title')} className="px-4 py-3 cursor-pointer text-xs font-semibold text-gray-500 uppercase tracking-wider select-none hover:text-gray-700 transition-colors w-[30%]">
                Title <SortIcon columnKey="title" />
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[14%]">Subject</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[20%]">Tags</th>
              <th onClick={() => handleSort('modifiedAt')} className="px-4 py-3 cursor-pointer text-xs font-semibold text-gray-500 uppercase tracking-wider select-none hover:text-gray-700 transition-colors w-[13%]">
                Updated <SortIcon columnKey="modifiedAt" />
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[13%]">Rating</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[10%]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {paginatedResources.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-10 text-gray-400 text-sm">
                  No resources match the current filters.
                </td>
              </tr>
            ) : (
              paginatedResources.map((resource) => (
                <tr key={resource.id} className="hover:bg-gray-50/70 transition-colors">
                  <td className="px-4 py-3 break-words" style={{wordBreak:'break-word'}}>
                    <button
                      onClick={() => handleResourceClick(resource)}
                      className="text-brand-600 hover:text-brand-700 hover:underline font-medium transition-colors text-left"
                    >
                      {resource.title}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{resource.subjectTitle || '—'}</td>
                  <td className="px-4 py-3 break-words" style={{wordBreak:'break-word'}}>
                    <div className="flex flex-wrap gap-1">
                      {resource.tags && resource.tags.length > 0
                        ? resource.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">{tag}</span>
                          ))
                        : <span className="text-gray-300">—</span>}
                      {resource.tags && resource.tags.length > 3 && (
                        <span className="text-xs text-gray-400">+{resource.tags.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {resource.modifiedAt
                      ? typeof resource.modifiedAt === 'string'
                        ? new Date(resource.modifiedAt).toLocaleDateString()
                        : resource.modifiedAt.toDate().toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <InlineRating document={resource} docRef={resource._ref} />
                  </td>
                  <td className="px-4 py-3">
                    <ReportAction document={resource} docRef={resource._ref} onReportSuccess={handleReportSuccess} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-2">
        {paginatedResources.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            No resources match the current filters.
          </div>
        ) : (
          paginatedResources.map((resource) => (
            <div key={resource.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-card">
              <button
                onClick={() => handleResourceClick(resource)}
                className="text-brand-600 hover:text-brand-700 hover:underline font-semibold text-sm block break-words transition-colors text-left"
              >
                {resource.title}
              </button>
              {resource.subjectTitle && (
                <div className="text-xs text-gray-400 mt-1">{resource.subjectTitle}</div>
              )}
              {resource.tags && resource.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {resource.tags.slice(0, 4).map((tag, i) => (
                    <span key={i} className="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">{tag}</span>
                  ))}
                  {resource.tags.length > 4 && (
                    <span className="text-xs text-gray-400">+{resource.tags.length - 4}</span>
                  )}
                </div>
              )}
              <div className="text-xs text-gray-400 mt-2">
                {resource.modifiedAt
                  ? (typeof resource.modifiedAt === 'string' ? new Date(resource.modifiedAt).toLocaleDateString() : resource.modifiedAt.toDate().toLocaleDateString())
                  : '—'}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                <InlineRating document={resource} docRef={resource._ref} />
                <ReportAction document={resource} docRef={resource._ref} onReportSuccess={handleReportSuccess} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {sortedResources.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="py-1 px-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {ITEMS_PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>per page</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={safePage === 1}
              className="px-2 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >«</button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="px-2 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >‹</button>
            {getPageNumbers().map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                  page === safePage
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >{page}</button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="px-2 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >›</button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={safePage === totalPages}
              className="px-2 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >»</button>
          </div>

          <span className="text-xs text-gray-400">
            {startIdx + 1}–{Math.min(startIdx + itemsPerPage, sortedResources.length)} of {sortedResources.length}
          </span>
        </div>
      )}
    </div>
  );
}
